import axios, { type AxiosError } from 'axios';

import awsconfig from '../aws-config';

/**
 * REST client keyed by logical API name. Does not authenticate: every call site
 * threads an explicit `token` and sets its own `Authorization` header. Argument one
 * contributes only NAMED-ENDPOINT RESOLUTION against the `API.endpoints` table in
 * `aws-config.ts`.
 *
 * Five semantics callers depend on:
 *
 *   - PERCENT-ENCODES THE QUERY STRING per parameter, so call sites write FHIR's
 *     reserved characters (`|`, `:`, `,`) literally. See `encodeQuery`.
 *   - REJECTS ON NON-2XX with an AxiosError, so `ErrorResponse.getMessage`'s
 *     `axios.isAxiosError` branch recovers the server message.
 *   - RESOLVES TO `response.data`, not the whole response — unlike
 *     `ConnectFetcher`/`ConnectPoster`/`services/connect.ts`, which return the whole
 *     response.
 *   - SERIALISES `body` and sets `Content-Type: application/json; charset=UTF-8`, with
 *     the caller's own headers layered on top so a caller-supplied `Content-Type` wins.
 *   - REJECTS ON AN UNKNOWN API NAME with a bare STRING, not an Error.
 *     `ErrorResponse.getMessage` has a `typeof error === 'string'` branch that this
 *     lands in, so the string is load-bearing.
 */

/** Request options: the subset of request configuration this app uses. */
export interface ApiRequestInit {
  headers?: Record<string, string | undefined>;
  body?: unknown;
}

/**
 * What a rejected call from this module actually carries — NOT an `Error`.
 *
 * The two rejection paths documented above disagree in kind: an unknown API name
 * rejects with a BARE STRING, a non-2xx response rejects with an `AxiosError`. Only
 * the second carries `.message`.
 *
 * Name this as SWR's `Error` type parameter. Reading `.message` off it is then a
 * compile error on the `string` branch — which is the point, not an obstacle.
 *
 * SUSPECTED DEFECT (widespread): dozens of call sites render `{someError.message}`
 * straight out of an SWR `error`, so the failure panel shows its heading and an EMPTY
 * message whenever the rejection took the string path. `utils/ErrorResponse.getMessage`
 * already handles all three shapes and is what those sites should route through. Left
 * unfixed throughout this pass — calling it changes what renders.
 */
export type ApiFetchError = AxiosError | string;

/**
 * Resolve a logical API name to its base URL via the `aws-config.ts` endpoints
 * table.
 *
 * An unmatched name throws rather than falling back to an invented base URL.
 *
 * `apiName` is `string | undefined` because that is what call sites pass: every one
 * reads an optional `import.meta.env.VITE_*_API_NAME`, whose presence is a build-time
 * guarantee via `service.manifest.json`, not a type-level one. An `undefined` name
 * matches no entry and takes the "does not exist" path. Both `name` and `endpoint` in
 * the table are `string | undefined` for the same reason.
 */
export const resolveEndpoint = (apiName: string | undefined): string => {
  const match = awsconfig.API.endpoints.find((entry) => entry.name === apiName);
  if (!match) {
    throw new Error(`API ${apiName} does not exist`);
  }
  // An unset endpoint yields the literal string "undefined/path" rather than an
  // error. Deliberate: making this throw would be a behaviour change.
  return String(match.endpoint);
};

/**
 * Percent-encode each query parameter's name and value, leaving the `&` and `=` that
 * separate them alone.
 *
 * axios sends the URL it is given verbatim, so a call site's literal `|` (FHIR's
 * system/value separator) reaches CloudFront raw and is answered with a bodyless 400
 * carrying no CORS headers — which the browser reports as a CORS failure. Amplify's
 * `API`, which this client replaced, encoded here; every call site was written against
 * that and is unchanged.
 *
 * `URLSearchParams` decodes before re-encoding, so an already-encoded value is left
 * alone rather than double-encoded. Its `+` for a space is rewritten to `%20`: that is
 * the one place its `application/x-www-form-urlencoded` serialisation differs from
 * what Amplify put on the wire.
 *
 * A literal `+` does not survive that decode — it becomes a space. Call sites must send `%2B`.
 */
const encodeQuery = (path: string): string => {
  const queryStart = path.indexOf('?');
  if (queryStart === -1) {
    return path;
  }
  const query = new URLSearchParams(path.slice(queryStart + 1)).toString().replace(/\+/g, '%20');
  return `${path.slice(0, queryStart)}?${query}`;
};

/**
 * Drop falsy headers before dispatch. Every call site in this app sends
 * `Authorization`, so this filter is part of the current wire behaviour.
 */
const compactHeaders = (headers: Record<string, string | undefined>): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(headers)) {
    if (value) {
      out[key] = value;
    }
  }
  return out;
};

const request = async <T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  apiName: string | undefined,
  path: string,
  init: ApiRequestInit = {},
): Promise<T> => {
  // An unknown name rejects with a STRING, not an Error. `ErrorResponse`
  // (utils/ErrorResponse.ts) has a `typeof error === 'string'` branch that this lands
  // in, and many components route caught errors through it, so the string is
  // load-bearing. Rejecting with an Error is a behaviour change, not a cleanup.
  let url: string;
  try {
    url = resolveEndpoint(apiName) + encodeQuery(path);
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- callers depend on the string rejection; see above.
    return Promise.reject(error instanceof Error ? error.message : String(error));
  }

  const headers = compactHeaders({
    // Set whenever a body is present; a caller's explicit `Content-Type` wins.
    ...(init.body === undefined ? {} : { 'Content-Type': 'application/json; charset=UTF-8' }),
    ...init.headers,
  });

  const response = await axios.request<T>({
    method,
    url,
    headers,
    // Serialising here rather than letting axios do it keeps the exact bytes on
    // the wire, including for a body that is already a string.
    ...(init.body === undefined ? {} : { data: JSON.stringify(init.body) }),
    responseType: 'json',
  });

  return response.data;
};

/**
 * THE TYPE ARGUMENT IS AN UNCHECKED CLAIM, NOT VALIDATION: a call site naming `T`
 * states what it believes the server sends. The default is `unknown`, so a call site
 * that states nothing must narrow at the point of use.
 */
export const apiClient = {
  get: <T = unknown>(
    apiName: string | undefined,
    path: string,
    init?: ApiRequestInit,
  ): Promise<T> => request<T>('GET', apiName, path, init),
  post: <T = unknown>(
    apiName: string | undefined,
    path: string,
    init?: ApiRequestInit,
  ): Promise<T> => request<T>('POST', apiName, path, init),
  put: <T = unknown>(
    apiName: string | undefined,
    path: string,
    init?: ApiRequestInit,
  ): Promise<T> => request<T>('PUT', apiName, path, init),
  del: <T = unknown>(
    apiName: string | undefined,
    path: string,
    init?: ApiRequestInit,
  ): Promise<T> => request<T>('DELETE', apiName, path, init),
};
